import React, { useCallback, useEffect } from "react";
import { FormProvider, useForm, UseFormSetError } from "react-hook-form";
import {
  Button,
  ButtonsContainer,
  FormGenericErrorMessage,
  FormRow,
  InputText,
  Select
} from "@silversea-ssc/ui-components-react";
import PhoneInputControl from "components/PhoneInputControl";
import { Trans, useLingui } from "@lingui/react/macro";
import InputWithError from "components/Form/InputWithError";
import * as styles from "./WebQuoteForm.module.scss";
import AsyncForm from "components/Form/AsyncForm";
import { FooterDisclaimer } from "components/WebQuoteModal/FooterDisclaimer/FooterDisclaimer";
import {
  generateFormTrackingId,
  sendWebQuote,
  WebQuoteError
} from "components/WebQuoteModal/utils";
import { useFriendlyCaptchaStatus } from "components/WebQuoteModal/FriendlyCaptcha/FriendlyCaptchaProvider";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { WebQuoteFormState } from "components/WebQuoteModal/WebQuoteModal";
import {
  getVisitorAuid,
  trackWebQuoteFormSubmitted
} from "src/utils/analytics/tracking";
import * as Validation from "@silversea-ssc/dotcom-api-types/Validation";

type CustomerInfo = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  comments?: string;
};

export type WebQuoteFormData = CustomerInfo;

type WebQuoteFormProps = {
  quoteProcessId: string;
  cruiseCode: string;
  quoteApi: string;
  setFormSubmitted: (webQuoteFormState: WebQuoteFormState) => void;
  validationError: WebQuoteError | undefined;
};

export const WebQuoteForm: React.FC<WebQuoteFormProps> = ({
  quoteProcessId,
  cruiseCode,
  quoteApi,
  setFormSubmitted,
  validationError
}) => {
  const defaultValues = {
    title: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    phoneNumber: undefined,
    comments: undefined
  };

  const formMethods = useForm<WebQuoteFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: true,
    shouldFocusError: true,
    criteriaMode: "all",
    defaultValues: defaultValues
  });

  const { formState, register, setError } = formMethods;
  const { i18n, t } = useLingui();
  const { captchaSolution } = useFriendlyCaptchaStatus();

  useSetErrors(validationError, setError);

  const onSubmit = useCallback(
    async (formData: WebQuoteFormData) => {
      const isValid = formState.isValid;
      if (!isValid) {
        setFormSubmitted({ validationError: undefined, submitted: false });
        return false;
      }
      const auid = getVisitorAuid();
      const trackingId = generateFormTrackingId();
      //const auid = "test

      const result = await sendWebQuote(
        quoteApi,
        quoteProcessId,
        captchaSolution,
        cruiseCode,
        formData,
        auid,
        trackingId
      );

      if (result.type === "success") {
        await trackWebQuoteFormSubmitted(
          {
            formName: "WebQuoteForm",
            phone_number_is_present:
              formData.phoneNumber !== undefined && formData.phoneNumber !== "",
            uwrid: trackingId,
            auid,
            request_type: "WEBQ",
            is_test: result.isTest
          },
          formData
        );
      }

      setFormSubmitted({
        validationError: result.type === "error" ? result.errorCode : undefined,
        submitted:
          result.type === "success" ||
          result.errorCode === "fatal" ||
          result.errorCode === "transient"
      });

      return result.type === "success";
    },
    [formState.isValid, captchaSolution]
  );

  return (
    <FormProvider {...formMethods}>
      <AsyncForm
        name="WebQuoteForm"
        onSubmit={onSubmit}
        className={styles.form}
        dataContentName={"web-quote-modal-form"}
      >
        <InputWithError
          id="subscribe-modal-email"
          name="email"
          label={`${t`Form Email`} *`}
        >
          <InputText
            {...register("email", {
              required: i18n._(t`form-required-email-address`),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: i18n._(t`form-invalid-email-address`)
              }
            })}
            type="email"
            autoComplete="email"
            disabled={!captchaSolution && formState.isSubmitting}
          />
        </InputWithError>
        <FormRow columns="3">
          <InputWithError
            name="title"
            id="web-quote-modal-title"
            label={`${t`Form Title`} *`}
          >
            <Select
              {...register("title", {
                required: i18n._(t`form-required-title`)
              })}
              dataContentPiece="form_title_select"
              defaultValue=""
              autoComplete="honorific-prefix"
              disabled={!captchaSolution && formState.isSubmitting}
            >
              <option value=""></option>
              <option value="Mr">{t`Form Title Mr.`}</option>
              <option value="Mrs">{t`Form Title Mrs.`}</option>
              <option value="Miss">{t`Form Title Miss.`}</option>
              <option value="Ms">{t`Form Title Ms.`}</option>
              <option value="Dr">{t`Form Title Dr.`}</option>
              <option value="Dir">{t`Form Title Dir.`}</option>
              <option value="Lady">{t`Form Title Lady.`}</option>
              <option value="Prof">{t`Form Title Prof.`}</option>
              <option value="Lord">{t`Form Title Lord.`}</option>
            </Select>
          </InputWithError>

          <InputWithError
            name="firstName"
            id="web-quote-modal-name-first"
            label={`${t`Form First name`} *`}
            autocomplete="given-name"
          >
            <InputText
              {...register("firstName", {
                required: i18n._(t`form-required-firstname`),
                pattern: {
                  value: Validation.FIRST_NAME_REGEX,
                  message: i18n._(t`form-invalid-characters`)
                }
              })}
              type="text"
              autoComplete="given-name"
              disabled={!captchaSolution && formState.isSubmitting}
            />
          </InputWithError>

          <InputWithError
            name="lastName"
            id="web-quote-modal-name-last"
            label={`${t`Form Last name`} *`}
          >
            <InputText
              {...register("lastName", {
                required: i18n._(t`form-required-lastname`),
                pattern: {
                  value: Validation.LAST_NAME_REGEX,
                  message: i18n._(t`form-invalid-characters`)
                }
              })}
              type="text"
              autoComplete="family-name"
              disabled={!captchaSolution && formState.isSubmitting}
            />
          </InputWithError>
        </FormRow>

        <PhoneInputControl name="phone" id="web-quote-modal-phone" required />

        <div className={styles.requiredFieldsLabel}>
          *<Trans>Required fields</Trans>
        </div>

        <FooterDisclaimer validationError={validationError} />

        <div className={styles.footer}>
          {!formState.isValid && formState.isSubmitted && (
            <FormGenericErrorMessage message="One or more fields have an error" />
          )}
          <ButtonsContainer align="center" spacingTop="regular">
            <Button
              className={styles.bodyFooterButtonHalf}
              renderAs="button"
              type="submit"
              loading={formState.isSubmitting}
              data-testid="submit"
              dataContentPiece="submit_button"
              disabled={!captchaSolution}
            >
              <Trans>Request</Trans>
            </Button>
          </ButtonsContainer>
        </div>
      </AsyncForm>
    </FormProvider>
  );
};

const useSetErrors = (
  validationError: WebQuoteError | undefined,
  setError: UseFormSetError<WebQuoteFormData>
) => {
  const invalidEmailMessage = i18n._(t`form-invalid-email-address`);

  useEffect(() => {
    if (validationError === "invalidEmail") {
      setError("email", {
        type: "invalid",
        message: invalidEmailMessage
      });
    }
  }, [setError, validationError]);
};
